import {
  PUZZLE_BOARD_COLUMNS,
  PUZZLE_BOARD_ROWS,
  PUZZLE_BUBBLE_RADIUS,
  PUZZLE_HEX_ROW_HEIGHT,
} from "./constants";
import type { PuzzleCell } from "./types";

export interface PuzzlePoint {
  x: number;
  y: number;
}

export interface PuzzleGridPosition {
  row: number;
  col: number;
}

export function puzzleCellKey(
  cell: Pick<PuzzleCell, "row" | "col">,
): string {
  return `${cell.row}:${cell.col}`;
}

export function isPuzzleCellInBounds(row: number, col: number): boolean {
  return (
    Number.isInteger(row) &&
    Number.isInteger(col) &&
    row >= 0 &&
    row < PUZZLE_BOARD_ROWS &&
    col >= 0 &&
    col < PUZZLE_BOARD_COLUMNS
  );
}

export function puzzleCellToPoint(
  cell: Pick<PuzzleCell, "row" | "col">,
): PuzzlePoint {
  return {
    x:
      PUZZLE_BUBBLE_RADIUS +
      cell.col * PUZZLE_BUBBLE_RADIUS * 2 +
      (cell.row % 2 === 1 ? PUZZLE_BUBBLE_RADIUS : 0),
    y: PUZZLE_BUBBLE_RADIUS + cell.row * PUZZLE_HEX_ROW_HEIGHT,
  };
}

/**
 * Returns neighbours in a stable order. Odd rows are shifted one radius right.
 * Keeping this integer topology authoritative avoids float-derived grid drift.
 */
export function getPuzzleNeighborPositions(
  row: number,
  col: number,
): PuzzleGridPosition[] {
  const diagonalColumns = row % 2 === 0 ? [col - 1, col] : [col, col + 1];
  const candidates: PuzzleGridPosition[] = [
    { row, col: col - 1 },
    { row, col: col + 1 },
    { row: row - 1, col: diagonalColumns[0] ?? col },
    { row: row - 1, col: diagonalColumns[1] ?? col },
    { row: row + 1, col: diagonalColumns[0] ?? col },
    { row: row + 1, col: diagonalColumns[1] ?? col },
  ];

  return candidates.filter((candidate) =>
    isPuzzleCellInBounds(candidate.row, candidate.col),
  );
}

export function sortPuzzleCells(cells: PuzzleCell[]): void {
  cells.sort((left, right) => left.row - right.row || left.col - right.col);
}

export function getPuzzleLegalSnapCells(
  cells: ReadonlyArray<PuzzleCell>,
): PuzzleGridPosition[] {
  const occupied = new Set(cells.map(puzzleCellKey));
  const result: PuzzleGridPosition[] = [];

  for (let row = 0; row < PUZZLE_BOARD_ROWS; row += 1) {
    for (let col = 0; col < PUZZLE_BOARD_COLUMNS; col += 1) {
      const candidate = { row, col };
      if (occupied.has(puzzleCellKey(candidate))) {
        continue;
      }
      if (
        row === 0 ||
        getPuzzleNeighborPositions(row, col).some((neighbor) =>
          occupied.has(puzzleCellKey(neighbor)),
        )
      ) {
        result.push(candidate);
      }
    }
  }

  return result;
}
